import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

export const resolvedFilingThreadSchema = z.object({
  threadId: z.uuid(),
  referenceNumber: z.number().int().positive(),
  referenceCode: z.string().describe("Human-readable code, e.g. COL-46"),
  status: z.string().describe("Thread status at filing time (open/resolved)"),
  /**
   * Match diagnostics (confidence, reasoning, signals) recorded on the evidence
   * + timeline for content-auto-matched threads. Absent for rep COL-N filings.
   */
  matchMetadata: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional()
    .describe(
      "Match diagnostics for auto-matched threads; absent for rep filings",
    ),
});

/**
 * Resolve Collection Filing Target Step
 *
 * The authorization + resolution gate for email-in evidence filing:
 * - Resolves the email sender to an active org user (the security boundary —
 *   only org users may file evidence; external senders stop here silently).
 * - Resolves the parsed COL-N references to collection threads in the org.
 */
export const resolveCollectionFilingTargetStep = defineStepConfig({
  type: "resolve_collection_filing_target",
  name: "Resolve Collection Filing Target",
  description:
    "Authorizes the email sender as an org user and resolves COL-N references to collection threads",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The forwarded email carrying the filing instruction",
    ),
    threadRefs: z
      .array(z.number().int().positive())
      .describe("Parsed collection thread reference numbers (the N in COL-N)"),
  }),

  outputSchema: z.object({
    authorized: z
      .boolean()
      .describe(
        "True when the email sender resolved to an active user in the organization",
      ),
    actorUserId: z
      .uuid()
      .nullable()
      .describe("The org user who requested the filing; null if unauthorized"),
    threads: z
      .array(resolvedFilingThreadSchema)
      .describe("Collection threads resolved from the parsed references"),
    unresolvedRefs: z
      .array(z.number().int().positive())
      .describe("Reference numbers that did not match any thread in the org"),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
